/**
 * Shared DOM utilities for dropdown discovery (used by useInfiniteScroll).
 *
 * Extracted to `shared/utils` to avoid circular dependency chains between
 * DatastoreDataProvider → FormPreview → FormBuilder → DatastoreDataProvider.
 */

/**
 * Escapes a string for safe use in a CSS attribute selector [attr="value"].
 * Handles " and \ which would otherwise break the selector.
 * Uses manual escape to avoid reliance on CSS.escape (which may not be in bundle).
 */
export const escapeForCssAttributeSelector = (value: string): string =>
  String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

/**
 * Checks if an element is actually visible in the viewport
 * (not just in DOM, but rendered and visible to user)
 */
const isElementVisible = (element: HTMLElement): boolean => {
  // Check if element has dimensions (not display: none)
  if (element.offsetWidth === 0 || element.offsetHeight === 0) {
    return false;
  }

  // Check computed style for visibility
  const style = window.getComputedStyle(element);

  return !(
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    style.opacity === '0'
  );
};

/**
 * Finds visible dropdown elements by looking for common dropdown menu patterns.
 * Only returns elements that are actually visible to the user (not just in DOM).
 *
 * @param dataTestId - The data-testid attribute to help locate the dropdown
 * @returns The dropdown menu element if found and visible, null otherwise
 */
export const findDropdownElement = (dataTestId: string): HTMLElement | null => {
  const componentWrapper = document.querySelector(
    `[data-testid="${escapeForCssAttributeSelector(dataTestId)}"]`
  );

  if (componentWrapper) {
    // Look for dropdown menu inside the component
    // MenuInput typically renders dropdown with role="listbox" or similar patterns
    const dropdownByRole = componentWrapper.querySelector<HTMLElement>(
      '[role="listbox"], [role="menu"]'
    );

    if (dropdownByRole && isElementVisible(dropdownByRole)) {
      return dropdownByRole;
    }

    // Look for common dropdown class patterns (works with CSS modules partial matches)
    const dropdownByClass = componentWrapper.querySelector<HTMLElement>(
      '[class*="dropdown"], [class*="Dropdown"], [class*="menu"], [class*="Menu"], [class*="listbox"]'
    );

    if (dropdownByClass && isElementVisible(dropdownByClass)) {
      return dropdownByClass;
    }
  }

  // Fallback: search globally for recently opened dropdowns
  // This handles portaled dropdowns that render outside the component tree
  const portaledDropdowns = Array.from(
    document.querySelectorAll<HTMLElement>('[role="listbox"], [role="menu"]')
  );

  const visibleDropdown = portaledDropdowns.find((dropdown) =>
    isElementVisible(dropdown)
  );

  return visibleDropdown ?? null;
};
